import { Button, DialogTrigger, Divider, Flex, Grid, Meter } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { shallow } from 'zustand/shallow';

import { Case, SwitchContainer, WithSkeleton } from '@exchange-frontends/components';

import { useStore } from '../../../store';
import { useMyReview } from '../../../utils/hooks';
import { UserDataContext } from '../../App';
import { LeaveReview } from './LeaveReview';
import MyReviewSection from './MyReviewSection';
import { Label } from './RatingsAndReviews';

const AcquirePluginWarning = styled.div`
  text-align: center;
  max-width: 200px;
  box-sizing: border-box;
  padding: 0 15px 0 15px;
`;

const BoldText = styled.strong`
  font-size: 20px;
`;

const HorizontalDivider = () => <Divider gridArea="divider" orientation="horizontal" size="S" width="100%" />;
const VerticalDivider = () => <Divider gridArea="divider" orientation="vertical" size="S" height="100%" />;

const RatingBar = ({ starNumber, numberOfReviews, totalReviews }) => (
  <Meter
    data-testid="rating-bar"
    label={`${starNumber} stars`}
    formatOptions={{ style: 'decimal' }}
    value={numberOfReviews}
    valueLabel={`${numberOfReviews} ratings`}
    minValue={0}
    maxValue={totalReviews || undefined}
    marginBottom="3px"
  />
);

const TEXT_REVIEW_WAS_SUBMITTED = 'Your review has been submitted!';
const TEXT_YOUR_RATING = 'Your rating';

const storeAttributesSelector = (state) => ({
  /**
   * @type {bool}
   */
  acquired: state.acquired,
  /**
   * @type {bool}
   */
  trialAcquired: state.trialAcquired,
  /**
   * @type {number}
   */
  totalRatings: state.totalRatings,
  /**
   * @type {number}
   */
  overallRating: state.overallRating,
  /**
   * @type {bool}
   */
  isMyReviewLoading: state.isMyReviewLoading,
});

const RatingsSection = ({ shouldShowRnr }) => {
  const { matchedBreakpoints } = useBreakpoint();
  const isMobileSized = !matchedBreakpoints.includes('M');

  const { profile, accessToken } = useContext(UserDataContext) ?? {};
  const { id: assetId } = useParams();
  const { acquired, totalRatings, overallRating, isMyReviewLoading } = useStore(storeAttributesSelector, shallow);
  const currentUserReview = useStore((state) => state.currentUserReview, shallow);
  const {
    rating1 = 0,
    rating2 = 0,
    rating3 = 0,
    rating4 = 0,
    rating5 = 0,
  } = useStore((state) => state.histogram ?? {}, shallow);

  const [ratingLabel, setRatingLabel] = useState(TEXT_YOUR_RATING);
  const [isUserReviewRendered, setIsUserReviewRendered] = useState(false);
  const [isUserRatingReadOnly, setIsUserRatingReadOnly] = useState(false);

  const isPluginAcquired = accessToken && acquired;
  const { error: _, isLoading } = useMyReview(accessToken, profile?.userId, assetId, isPluginAcquired);
  const hasReview = currentUserReview?.title;
  const linkText = isUserReviewRendered ? 'Hide review' : 'View or edit review';

  const onSuccessfulReview = () => {
    setRatingLabel(TEXT_REVIEW_WAS_SUBMITTED);
    setTimeout(() => {
      setRatingLabel(TEXT_YOUR_RATING);
    }, 3000);
  };

  useEffect(() => {
    useStore.setState({
      isMyReviewLoading: isLoading,
    });
  }, [isLoading]);

  useEffect(() => {
    if (hasReview) {
      setIsUserReviewRendered(true);
    }
  }, [hasReview]);

  useEffect(() => {
    if (!isPluginAcquired && isMyReviewLoading) {
      useStore.setState({
        isMyReviewLoading: false,
      });
    }
  }, [isMyReviewLoading, isPluginAcquired]);

  return (
    <>
      <Grid
        data-testid="ratings-ssection-grid"
        areas={isMobileSized ? ['count-by-stars', 'divider', 'stars'] : ['stars divider count-by-stars']}
        columns={isMobileSized ? ['auto'] : ['1fr', 'auto', '1fr']}
        alignItems="center"
        rows={isMobileSized ? ['1fr', 'auto', '1fr'] : ['200px']}
        gap="size-400"
      >
        <Flex direction="column" alignItems="center" gridArea="stars" gap="size-500">
          <Flex>
            {overallRating > 0 && <BoldText>{overallRating.toFixed(1)}</BoldText>}
            <Flex direction="column" alignItems="center" gap="size-200" minWidth="160px">
              <Flex direction="column" alignItems="center">
                <LeaveReview
                  variant="rating"
                  readOnly
                  fixedValue={parseInt(overallRating)}
                  label={`${totalRatings} rating${totalRatings === 1 ? '' : 's'}`}
                />
              </Flex>
              {shouldShowRnr && isPluginAcquired && (
                <Flex direction="column" alignItems="center">
                  <WithSkeleton shouldRender={!isLoading} fullWidthContainer height="20px">
                    <LeaveReview variant="rating" readOnly={isUserRatingReadOnly} label={ratingLabel} />
                  </WithSkeleton>
                  <SwitchContainer>
                    <Case when={hasReview}>
                      <Button
                        variant="accent"
                        onPress={() => {
                          setIsUserReviewRendered((state) => !state);
                        }}
                        marginTop="size-100"
                      >
                        {linkText}
                      </Button>
                    </Case>
                    <Case when={!hasReview}>
                      <DialogTrigger>
                        <Button
                          variant="accent"
                          onPress={() => {
                            setIsUserRatingReadOnly(true);
                          }}
                          marginTop="size-100"
                        >
                          Leave a review
                        </Button>
                        {(close) => (
                          <LeaveReview
                            onSuccessfulReview={onSuccessfulReview}
                            close={() => {
                              setIsUserRatingReadOnly(false);
                              close();
                            }}
                          />
                        )}
                      </DialogTrigger>
                    </Case>
                  </SwitchContainer>
                </Flex>
              )}
            </Flex>
          </Flex>
          {shouldShowRnr && !isPluginAcquired && (
            <AcquirePluginWarning>
              <Label>Please download and install this plugin before submitting a rating or review.</Label>
            </AcquirePluginWarning>
          )}
        </Flex>
        {isMobileSized ? <HorizontalDivider /> : <VerticalDivider />}
        <Flex direction="column" alignItems="center" gridArea="count-by-stars" gap="size-100">
          <RatingBar starNumber={5} totalReviews={totalRatings} numberOfReviews={rating5} />
          <RatingBar starNumber={4} totalReviews={totalRatings} numberOfReviews={rating4} />
          <RatingBar starNumber={3} totalReviews={totalRatings} numberOfReviews={rating3} />
          <RatingBar starNumber={2} totalReviews={totalRatings} numberOfReviews={rating2} />
          <RatingBar starNumber={1} totalReviews={totalRatings} numberOfReviews={rating1} />
        </Flex>
      </Grid>
      {shouldShowRnr && isUserReviewRendered && (
        <MyReviewSection currentUserReview={currentUserReview} setIsUserReviewRendered={setIsUserReviewRendered} />
      )}
    </>
  );
};

export default RatingsSection;
